import React, { JSX, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import './TableData.scss';
import { IDocumentTable, TableColumn } from '@models/Forms/IForms';
import DataGrid, {
    Column,
    ColumnChooser,
    DataGridHeaderFilterSearch,
    Export,
    FilterRow,
    Grouping,
    HeaderFilter,
    Item,
    MasterDetail,
    Pager,
    Paging,
    Scrolling,
    Selection,
    Sorting,
    Toolbar,
} from 'devextreme-react/data-grid';
import ArrayStore from 'devextreme/data/array_store';
import DataSource from 'devextreme/data/data_source';
import DevExpressDataGrid from '../DevExpress/DataGrid/DevExpressDataGrid';
import { Template } from 'devextreme-react/core/template';
import RowDetailsButton from './RowDetailsButton';
import { IField } from '@models/IFormData';
import { IActionService } from '@services/actions/IActionService';
import { IBaseAction } from '@models/actions/IBaseAction';
import { useParams } from 'react-router';
import clsx from 'clsx';
import {
    checkGridFilterValue,
    CustomizeExcelCellProps,
    exportGridToExcel,
    getColumnDataTypeByFieldDataType,
    getColumnHeaderFilterStateByFieldDataType,
    onCellHoverChanged,
} from '@utils/devextreme/dataGridUtils';
import ViewCellRenderSwitcher from './ViewCellRenderSwitcher';
import ActionButton from '@atoms/DevExpress/Buttons/ActionButton/ActionButton';
import OpenDocButton from './OpenDocButton';
import Tooltip from '@atoms/Tooltip';
import { MdHelpOutline } from 'react-icons/md';
import { CellPreparedEvent, OptionChangedEvent } from 'devextreme/ui/data_grid';
import { ValueType } from '@/types/ValueType';
import { TabsContext } from '@/context/Contexts';
import { useDataGridScrollToLeftButton } from '@hooks/useDataGridScrollToLeftButton/useDataGridScrollToLeftButton';
import CopyColumnContentButton from '@atoms/TableData/CopyColumnContentButton';
import { useAnalytics } from '@/analytics';
import ClearFilterButton from '@atoms/DevExpress/Buttons/ClearFilterButton/ClearFilterButton';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { useLocalStorage } from 'usehooks-ts';
import { findColumnByKey } from '@utils/tableHelper';

export interface ITableViewProps {
    value?: any[];
    table: IDocumentTable;
    parentTable?: IDocumentTable;
    cellRenderSwitcher: (p: any, column: any, rowParent?: any) => Promise<React.ReactNode>;
    cellTextValueSwitcher: (p: any, column: any) => string;
    visibilityColumn: (rules: string, rowParent?: any) => Promise<boolean>;
    getParentFields: () => IField[];
    actionService?: IActionService<IBaseAction>;
    onClickAction?: () => void;
    onRefreshExternalDS?: () => Promise<void>;
    docId?: string;
}

const allowedPageSizes: (number | 'all' | 'auto')[] = [5, 10, 20, 'all'];

const TableView: React.FC<ITableViewProps> = ({
    value,
    table,
    parentTable,
    cellRenderSwitcher,
    cellTextValueSwitcher,
    visibilityColumn,
    getParentFields,
    onClickAction,
    onRefreshExternalDS,
    actionService,
    docId,
}: ITableViewProps) => {
    const currentUser = useTypedSelector((state) => state.auth.user);
    const [scrollByContent] = useLocalStorage<boolean>(`user-${currentUser.username}.scrollByContentInTables`, false);

    const { analytics } = useAnalytics();

    const activated = React.useRef(false);
    const { id } = useParams();
    const gridRef = useRef<DataGrid>(null);

    const [columns, setColumns] = useState<JSX.Element[]>();

    useEffect(() => {
        activated.current = true;
        InitColumns();
        return () => {
            activated.current = false;
        };
    }, []);

    // Принудительно обновляем размеры таблиц при смене вкладок
    const tabsContext = useContext(TabsContext);
    useEffect(() => {
        gridRef.current?.instance.updateDimensions();
    }, [tabsContext?.activeTab]);

    const renderDetailTable = useCallback(
        (param: any) => {
            return table.tables.map((subTable, i) => {
                let key = subTable.key;

                let val = param.data[key] ?? [];
                if (val.length === 0) return <></>;

                return (
                    <TableView
                        key={i}
                        table={subTable}
                        parentTable={table}
                        value={val}
                        getParentFields={getParentFields}
                        cellRenderSwitcher={cellRenderSwitcher}
                        cellTextValueSwitcher={cellTextValueSwitcher}
                        visibilityColumn={async (rules: string, rowParent?: any) => {
                            return await visibilityColumn(rules, param.data);
                        }}
                        docId={docId}
                    />
                );
            });
        },
        [cellRenderSwitcher, docId, getParentFields, table, table.tables, visibilityColumn],
    );

    const renderMasterDetail = useCallback(() => {
        return <MasterDetail enabled={true} render={renderDetailTable} />;
    }, [renderDetailTable]);

    const renderViewInFormActColumn = useCallback(
        (table: IDocumentTable, rows: any[]) => {
            return (
                <Column
                    key={`viewInForm`}
                    width="36px"
                    fixed={false}
                    visibleIndex={0}
                    encodeHtml={true}
                    allowResizing={false}
                    allowHiding={false}
                    allowReordering={false}
                    allowHeaderFiltering={false}
                    cssClass="dx-command-edit dx-command-edit-with-icons dx-cell-focus-disabled"
                    cellRender={(p) => {
                        return (
                            <RowDetailsButton
                                getParentFields={getParentFields}
                                table={table}
                                rowData={p.data}
                                docId={docId}
                                rowArray={rows}
                            />
                        );
                    }}
                />
            );
        },
        [docId, getParentFields],
    );

    const renderViewActionColumn = useCallback((table: IDocumentTable) => {
        return (
            <Column
                key={`viewActions`}
                caption="Действия"
                visibleIndex={100}
                width="88px"
                allowResizing={false}
                allowHiding={false}
                alignment="center"
                allowReordering={false}
                allowHeaderFiltering={false}
                cellTemplate="dxGridRowMenuCellTemplateAjax"
            />
        );
    }, []);

    const renderOpenDocActColumn = useCallback(
        (table: IDocumentTable) => {
            return (
                <Column
                    key={`openDoc`}
                    width="36px"
                    fixed={false}
                    visibleIndex={0}
                    encodeHtml={true}
                    allowResizing={false}
                    allowHiding={false}
                    allowReordering={false}
                    allowHeaderFiltering={false}
                    cssClass="dx-command-edit dx-command-edit-with-icons dx-cell-focus-disabled"
                    cellRender={(p) => {
                        return <OpenDocButton table={table} rowData={p.data} docId={docId} />;
                    }}
                />
            );
        },
        [docId],
    );

    const renderColumnGrid = useCallback(
        async (column: TableColumn, path: string, dataType: ValueType) => {
            return (
                <Column
                    key={`column${path}${column.key}`}
                    dataField={column.key}
                    caption={column.name}
                    dataType={getColumnDataTypeByFieldDataType(dataType)}
                    width={column.width}
                    minWidth={
                        // Если ширина в vw, то задаем мин ширину 5px
                        // У грида есть баг, он сравнивает width и minWidth только по цифре, без учета единиц
                        column.width ? (column.width.toString().endsWith('vw') ? 5 : undefined) : undefined
                    }
                    sortIndex={column.sortIndex ?? undefined}
                    sortOrder={column.sortOrder}
                    groupIndex={column.groupIndexView ?? undefined}
                    showWhenGrouped={column.showWhenGrouped}
                    groupCellRender={(options) => {
                        if (!column.groupCellTemplate) return `${options.column.caption}: ${options.text}`;
                        let result = column.groupCellTemplate;
                        result = result.replaceAll('{caption}', options.column.caption);
                        result = result.replaceAll('{value}', options.text);
                        return result;
                    }}
                    allowHeaderFiltering={getColumnHeaderFilterStateByFieldDataType(dataType)}
                    visible={!column.hidden}
                    visibleIndex={column.order}
                    encodeHtml={true}
                    alignment={column.alignment}
                    //TODO попросили убрать
                    headerCellRender={(p) => {
                        return (
                            <div className="title-column-box">
                                <div
                                    className={clsx(
                                        'title-column-caption',
                                        column.headerNoEllipsis && 'title-column-caption-noEllipsis',
                                    )}
                                >
                                    {p.column.caption}
                                </div>
                                <div className="title-column-indicators">
                                    {column.viewTitle && (
                                        <div
                                            className={clsx(
                                                'title-column-indicator',
                                                'title-column-indicator__tooltip',
                                            )}
                                        >
                                            <Tooltip
                                                openDelay={100}
                                                background="black"
                                                position="bottom"
                                                maxWidth="40ch"
                                            >
                                                <MdHelpOutline size="16" />
                                                {column.viewTitle}
                                            </Tooltip>
                                        </div>
                                    )}
                                    {column.showCopyColumnContentButton && (
                                        <div className={clsx('title-column-indicator', 'title-column-indicator__copy')}>
                                            <CopyColumnContentButton
                                                column={column}
                                                gridRef={gridRef}
                                                separator={column.copyColumnContentSeparator}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    }}
                    cellRender={(p) => {
                        return (
                            <ViewCellRenderSwitcher data={p} column={column} cellRenderSwitcher={cellRenderSwitcher} />
                        );
                    }}
                >
                    <HeaderFilter
                        dataSource={(options) => {
                            const store = gridDataSource.store() as any;
                            const data = store._array;
                            options.dataSource!.postProcess = function (results) {
                                return results.map((result) => {
                                    // Получаем текстовое представление значения ячейки используя функцию cellTextValueSwitcher.
                                    // Необходимо для обработки displayFormat у dict и abook полей.
                                    const text =
                                        cellTextValueSwitcher(
                                            {
                                                data: data.find((item: any) => item[column.key] === result.key),
                                                value: result.value,
                                            },
                                            column,
                                        ) || '(Пустое)';
                                    return { ...result, text };
                                });
                            };
                        }}
                    />
                </Column>
            );
        },
        [cellRenderSwitcher, cellTextValueSwitcher],
    );

    const gridDataSource = useMemo(() => {
        return new DataSource({
            store: new ArrayStore({
                key: '|NUM',
                data: value!,
            }),
        });
    }, [value]);

    const checkVisRules = useCallback(
        async (column: any) => {
            return (
                column.visibilityRules === undefined ||
                column.visibilityRules === null ||
                (column.visibilityRules && (await visibilityColumn(column.visibilityRules)))
            );
        },
        [visibilityColumn],
    );

    const InitColumns = useCallback(
        async () => {
            let result: JSX.Element[] = [];
            let w = 50;
            for (let index = 0; index < table.tableColumn.length; index++) {
                const column = table.tableColumn[index];
                let vis = column.hidden || (await checkVisRules(column));
                if (vis) {
                    w = w + +column.width;
                    result.push(await renderColumnGrid(column, `table_grid_${table.key}`, column.valueType));
                }
            }
            for (let index = 0; index < table.tableColumnDict.length; index++) {
                const column = table.tableColumnDict[index];
                let vis = await checkVisRules(column);
                if (vis) {
                    w = w + +column.width;
                    result.push(await renderColumnGrid(column, `table_dictgrid_${table.key}`, ValueType.Text));
                }
            }
            for (let index = 0; index < table.tableColumnAbook.length; index++) {
                const column = table.tableColumnAbook[index];
                let vis = await checkVisRules(column);
                if (vis) {
                    w = w + +column.width;
                    result.push(await renderColumnGrid(column, `table_abookgrid_${table.key}`, ValueType.Text));
                }
            }
            for (let index = 0; index < table.tableColumnCalc.length; index++) {
                const column = table.tableColumnCalc[index];
                let vis = await checkVisRules(column);
                if (vis) {
                    result.push(await renderColumnGrid(column, `table_calcgrid_${table.key}`, ValueType.Double));
                }
            }
            for (let index = 0; index < table.tableColumnAutoComplete.length; index++) {
                const column = table.tableColumnAutoComplete[index];
                let vis = await checkVisRules(column);
                if (vis) {
                    result.push(await renderColumnGrid(column, `table_autocompletegrid_${table.key}`, ValueType.Text));
                }
            }
            for (let index = 0; index < table.tableColumnExDataSource.length; index++) {
                const column = table.tableColumnExDataSource[index];
                let vis = await checkVisRules(column);
                if (vis) {
                    w = w + +column.width;
                    result.push(await renderColumnGrid(column, `table_calcgrid_${table.key}`, column.valueType));
                }
            }
            for (let index = 0; index < table.tableColumnUrl.length; index++) {
                const column = table.tableColumnUrl[index];
                let vis = await checkVisRules(column);
                if (vis) {
                    w = w + +column.width;
                    result.push(await renderColumnGrid(column, `table_urlgrid_${table.key}`, ValueType.Text));
                }
            }

            if (activated.current) {
                setColumns(result);
            }
            return null;
        },
        // prettier-ignore
        [
        checkVisRules, renderColumnGrid, table.key, table.tableColumn, table.tableColumnAbook, table.tableColumnAutoComplete,
        table.tableColumnCalc, table.tableColumnDict, table.tableColumnExDataSource, table.tableColumnUrl
    ],
    );

    const customizeExcelCell = useCallback(
        (options: CustomizeExcelCellProps) => {
            const { gridCell, excelCell } = options;
            if (!gridCell || !excelCell) return;

            if (gridCell.rowType !== 'data') return;

            const dataField = gridCell.column?.dataField;
            if (!dataField) return;

            const column = findColumnByKey(table, dataField);
            if (!column) return;

            excelCell.value =
                cellTextValueSwitcher(
                    {
                        data: gridCell.data,
                        value: gridCell.value,
                    },
                    column,
                ) ?? '';
        },
        [cellTextValueSwitcher, table],
    );

    const onExporting = useCallback(() => {
        exportGridToExcel(gridRef, table.name || 'Экспорт таблицы', customizeExcelCell);

        analytics.trackEvent({
            category: 'DataTable',
            action: `Клик по кнопке экспорта таблицы данных '${table.name}'`,
            name: `document-table-export--${table.id}`,
            customDimensions: [{ id: 101, value: docId ?? '' }],
        });
        analytics.deleteCustomDimensions([101]);
    }, [analytics, docId, table.id, table.name, customizeExcelCell]);

    const onCellPrepared = useCallback(
        (e: CellPreparedEvent<any, any>) => {
            // Задача EUPDEV-8613
            // Компонент MasterDetail не умеет скрывать себя для отдельных строк master-таблицы, когда detail-часть пустая
            // Скрываем обходным путем

            // Находим ячейку, содержащую стрелку раскрытия detail-части
            if (e.column.type === 'detailExpand' && e.rowType === 'data') {
                // Определяем пустая detail-часть или нет
                const detailIsEmpty = table.tables.every((t) => {
                    let items = e.data[t.key];
                    return !items || items.length === 0;
                });
                if (detailIsEmpty) {
                    e.cellElement.firstChild?.remove(); // удаляем элемент со стрелкой из ячейки
                    e.cellElement.style.pointerEvents = 'none'; // запрещаем ячейке реагировать на нажатия
                }
            }
        },
        [table.tables],
    );

    const isSubTable = parentTable !== undefined;

    // -------------------------------------------------------------------------------------------------------------------
    // Initialize the scroll-to-left button hook
    const { containerProps, buttonContainerElement: scrollToLeftElement, setGridRef } = useDataGridScrollToLeftButton();

    // Connect the gridRef to the hook
    useEffect(() => {
        if (gridRef.current) {
            setGridRef(gridRef.current);
        }
    }, [gridRef, setGridRef]);
    // -------------------------------------------------------------------------------------------------------------------

    const [hasFilters, setHasFilters] = useState<boolean>(false);

    const onOptionChanged = useCallback((e: OptionChangedEvent<any, string>) => {
        const [gridFilterChanged, hasGridFilter] = checkGridFilterValue(e);
        if (gridFilterChanged) {
            setHasFilters(hasGridFilter);
        }
    }, []);

    // -------------------------------------------------------------------------------------------------------------------

    return columns ? (
        <div className="form-table" data-testid={table.id ? `table-view-${table.id}` : undefined} {...containerProps}>
            <DevExpressDataGrid
                ref={gridRef}
                columnMinWidth={30}
                allowColumnResizing={true}
                wordWrapEnabled={table.wordWrapEnabled ?? true}
                columnResizingMode="widget"
                dataSource={gridDataSource}
                onCellHoverChanged={onCellHoverChanged}
                onOptionChanged={onOptionChanged}
                onExporting={onExporting}
                onCellPrepared={onCellPrepared}
                showBorders={false}
            >
                <Scrolling useNative={!scrollByContent} showScrollbar="always" scrollByContent={scrollByContent} />
                <Grouping expandMode="rowClick" />
                <ColumnChooser enabled={false} />
                <Sorting mode="multiple" showSortIndexes={false} />
                <Toolbar visible={table.allowExport || (table.allowFiltersRow && hasFilters)}>
                    <Item location="after" key="clear_filter" visible={table.allowFiltersRow && hasFilters}>
                        <ClearFilterButton gridRef={gridRef} />
                    </Item>
                    <Item location="after" key="exportButton" name="exportButton" />
                </Toolbar>
                <Pager
                    showPageSizeSelector={!isSubTable}
                    allowedPageSizes={allowedPageSizes}
                    showInfo={true}
                    visible={isSubTable ? 'auto' : true}
                    showNavigationButtons={true}
                    displayMode={'full'}
                />
                <Paging defaultPageSize={table.pageSize && table.pageSize > 0 ? table.pageSize : 20} />
                <FilterRow showOperationChooser={true} visible={table.allowFiltersRow} />
                <HeaderFilter visible={table.allowFiltersRow} allowSelectAll={true} height={600} width={300}>
                    <DataGridHeaderFilterSearch enabled={true} />
                </HeaderFilter>
                {table.viewInForm && renderViewInFormActColumn(table, value!)}
                {table.previewDocByKey && renderOpenDocActColumn(table)}
                {columns}
                {table.tableColumnAction && table.tableColumnAction.linkDocumentAction && renderViewActionColumn(table)}
                {table.tables?.length > 0 && renderMasterDetail()}

                <Export
                    enabled={table.allowExport}
                    allowExportSelectedData={table.allowExportSelectedRows}
                    formats={['xlsx']}
                    texts={{
                        exportAll: table.exportAllText || undefined,
                        exportSelectedRows: table.exportSelectedRowsText || undefined,
                        exportTo: table.exportToText || undefined,
                    }}
                />
                {table.allowExport && table.allowExportSelectedRows ? (
                    <Selection
                        allowSelectAll={true}
                        mode={'multiple'}
                        selectAllMode={'allPages'}
                        showCheckBoxesMode={'always'}
                    />
                ) : null}

                <Template
                    name="dxGridRowMenuCellTemplateAjax"
                    render={function (data: any) {
                        if (!table.tableColumnAction) return <></>;
                        let key = id + '/' + data.data[table.tableColumnAction.linkDocumentAction.linkKey];

                        return (
                            <ActionButton
                                objId={key}
                                service={actionService}
                                objData={data.data}
                                onActionClick={() => {
                                    onClickAction && onClickAction();
                                }}
                                onModifyData={() => {
                                    onRefreshExternalDS && onRefreshExternalDS();
                                    data.component.refresh();
                                }}
                            />
                        );
                    }}
                />
            </DevExpressDataGrid>
            {!isSubTable && scrollToLeftElement}
        </div>
    ) : (
        <></>
    );
};

export default TableView;
